import React, { useEffect, useState } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { exerciseFields } from "../../stories/Forms/fields";
import { exerciseSchema } from "../../stories/Forms/validationSchema";
import { Row, Col } from "react-bootstrap";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  assessmentStatusUrl,
  assessmentValidateUrl,
  phlebotomyAssessmentStatusUrl,
} from "../../utils/constants";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import { XSquare } from "react-bootstrap-icons";
import { useStepDataProtection } from "./useStepDataProtection";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";

let submissionData;

/**
 * ExerciseForm Component
 *
 * A form component to handle the "Exercise" step of an assessment process.
 * Manages user input, validation, and navigation between steps.
 * @returns {JSX.Element} The rendered ExerciseForm component.
 */

function ExerciseForm() {
  const auth = useAuth();
  const { bid, test_type, test_kit_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const formId = location.pathname.split("/").at(-1);
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const [data, setData] = useState({});
  const isPhlebotomy = location.pathname.includes("boots");

  const { callApi, resultGet, resultPost, serverError } = useApis();

  useStepDataProtection(
    auth?.allStepData,
    resultGet,
    test_kit_id,
    location,
    navigate,
    auth?.showToast,
  );

  // Update progress steps when we get test data
  useEffect(() => {
    if (resultGet) {
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.test_kit_details?.test_kit_type,
        location.pathname
      );
      auth.setProgressSteps(newProgressSteps);
    }
  }, [resultGet, location.pathname]);

  useEffect(() => {
    if (!auth?.allStepData && resultGet) {
      auth.setAllStepData(resultGet?.assessment_values);
    }
  }, [resultGet]);

  const handleAssessmentSubmit = (values) => {
    // Remove keys with undefined values
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });

    submissionData = {
      activity: {
        physical_activity: values?.physical_activity,
        light_activity_hours_per_week:
          values?.light_activity_hours_per_week || null,
        moderate_activity_hours_per_week:
          values?.moderate_activity_hours_per_week || null,
        vigorous_activity_hours_per_week:
          values?.vigorous_activity_hours_per_week || null,
      },
      step: "activity",
    };

    // Update progress BEFORE API call
    const completedProgressSteps = updateProgressAfterCompletion(
      resultGet,
      resultGet?.test_kit_details?.test_kit_type,
      location.pathname
    );
    auth.setProgressSteps(completedProgressSteps);

    // Make API call
    if (submissionData) {
      callApi(
        {
          user_id: JSON.parse(localStorage.getItem("user_id")),
          ...submissionData,
        },
        `${assessmentValidateUrl}`,
        "POST",
        true,
      );
    }

    auth.setAllStepData((prevStep) => ({ ...prevStep, ...submissionData }));
  };

  useEffect(() => {
    setData(
      auth.allStepData?.activity || resultGet?.assessment_values?.activity,
    );
  }, [auth.allStepData, resultGet]);

  // Handle POST result - just navigate
  useEffect(() => {
    if (resultPost) {
      const nextFormId = Number.parseInt(formId) + 1;
      const destination =
        isPhlebotomy && test_type && bid
          ? `/bloods/boots/assessment/${test_type}/${bid}/${nextFormId}`
          : `/assessment/${test_kit_id}/${nextFormId}`;

      navigate(destination, { state: { ...auth.allStepData } });
    }
  }, [resultPost]);

  // Initial API call
  useEffect(() => {
    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
      localStorage.setItem("test_kit_id", bid);
    } else {
      if (location?.state?.test_kit_id){
        localStorage.setItem("test_kit_id", location?.state?.test_kit_id);
      }
      callApi(
        null,
        `${assessmentStatusUrl}${test_kit_id || localStorage.getItem("test_kit_id")}`,
        "GET",
        true,
      );
    }
  }, []);

  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  return (
    <div className="row justify-content-center start-assessment-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth.progressSteps.totalSteps}
          currentStep={auth.progressSteps.currentStep}
          completedSteps={auth.progressSteps.completedSteps}
          isCompleted={auth.progressSteps.completedSteps}
          is_assessment_completed={false}
        />
        <div className="container-fluid">
          <Row>
            <Col md={12} className="p-0">
              <div className="mt-5">
                <div className="assessment-form">
                  <DynamicForm
                    fields={exerciseFields}
                    blockRefresh={true}
                    modify={true}
                    initialValues={data}
                    validationSchema={exerciseSchema}
                    onSubmit={handleAssessmentSubmit}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

export default ExerciseForm;